import { useEffect, useState, useRef } from "react";
import { Paragraph } from "../Commons/Paragraph";
import { AiEmptyChat } from "../Chat/AiEmptyChat";
import useUserProfile from "../../hooks/useUserProfile";
import { actions } from "../../Redux/store";
import { useFirebaseRemoteParams } from "../../Context/FirebaseContext/FirebaseContext";
import { motion } from "framer-motion";
import type { Variants } from "framer-motion";
import advancedStats from "../../assets/imgs/stats-icon.svg";
import chatbotDown from "../../assets/imgs/chatbot-down.svg";
import chatbotDownBg from "../../assets/imgs/chatbot-down-bg.svg";
import { useSupabaseAuth } from "../../Context/AuthContext/AuthContext";
import { useMixpanel } from "../../Context/MixpanelContext/MixpanelProvider";

import selectplatform from "../../assets/imgs/select_platform_icon.png";
import { hapticsImpactLight } from "../../lib/utils";
import { useLeagueInfo } from "../../Context/LeagueInfoContext/LeagueInfoContext";
import { useLeagueStore } from "../../zustandStore/leagueStore";
import LeagueActiveDot from "../LeagueSync/LeagueSelectionModal/LeagueActiveDot";
import { useSocket } from "../../Context/SocketContext/SocketProvider";
import { useLocation, useNavigate } from "react-router-dom";
import AdvancedStatsPromo from "./AdvancedStatsModal/AdvancedStatsPromo";
import { useSelector } from "react-redux";
import { useAdvancedStatsPromoStore } from "../../zustandStore/advancedStatsPromoStore";
import { useRevenueCatAuth } from "../../Context/RevenueCatContext/RevenueCatContext";
import { useBookaCallStore } from "../../zustandStore/bookaCallStore";

export const AiChatbot = () => {
  const { firstName } = useUserProfile();
  const { useAuthStore } = useSupabaseAuth();
  const { screenData } = useFirebaseRemoteParams();
  const postFix = process.env.REACT_APP_ENV === "stagging" ? "_stg" : "_prod";
  const chatbotDefaultSuggestions = `chatbot_defaultSuggestions${postFix}`;
  const chatbotMaintenance = `chatbotMaintenance${postFix}`;
  const [dataItems, setDataItems] = useState<any[]>([]);
  const [isChatbotMaintenance, setIsChatbotMaintenance] =
    useState<boolean>(false);
  const isSuperUser = useAuthStore.getState().profile?.is_super_user;
  const Mixpanel = useMixpanel();
  const { filteredLeagueData, refetchLeagueInfo } = useLeagueInfo();
  const { selectedLeague } = useLeagueStore();
  const { startNewThread } = useSocket();
  const hasRefetchedRef = useRef(false);
  const hasStartedThreadRef = useRef(false);
  const hasIncrementedCountRef = useRef(false);
  const location = useLocation();
  const hasShowAnimation = location.state?.showAnimation || false;
  const isAdvancedStatsPromoPopup = useSelector(
    (state: any) => state.modal.advancedStatsPromoPopup?.open,
  );
  const { setHasSeenPromo, hasSeenPromo } = useAdvancedStatsPromoStore();
  const {
    isSubscribed,
  } = useRevenueCatAuth();
  const { count, incrementCount ,resetCount} = useBookaCallStore();
	// Determine whether to show the maintenance screen
	const showMaintenanceScreen = isChatbotMaintenance === true && !isSuperUser;
  const leagueAccess = isSuperUser || isSubscribed;
  const navigate = useNavigate();

  useEffect(() => {
    if (screenData && screenData[chatbotDefaultSuggestions]) {
      setDataItems(JSON.parse(screenData[chatbotDefaultSuggestions]));
    }

    if (screenData) {
      setIsChatbotMaintenance(screenData[chatbotMaintenance]);
    }
  }, [screenData]);

  // Call refetchLeagueInfo when component mounts (only once)
  useEffect(() => {
    if (!hasRefetchedRef.current) {
      refetchLeagueInfo();
      hasRefetchedRef.current = true;
    }
  }, []);



  // Start a new thread when the component mounts and when selectedLeague data is available (only once)
  useEffect(() => {
    if (
      selectedLeague?.leagueId &&
      selectedLeague?.ownerId &&
      !hasStartedThreadRef.current
    ) {
      startNewThread(selectedLeague.leagueId, selectedLeague.ownerId);
      hasStartedThreadRef.current = true;
    }
  }, [selectedLeague?.leagueId, selectedLeague?.ownerId, startNewThread]);

  // Auto-open AdvancedStatsPromo if user hasn't seen it
  useEffect(() => {
    if (!hasSeenPromo && !showMaintenanceScreen) {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        actions.modal.openAdvancedStatsPromoPopup(null);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [hasSeenPromo, showMaintenanceScreen]);

  // Handle BookaCall count increment and modal opening
  useEffect(() => {
    if (hasShowAnimation && isSubscribed && !hasIncrementedCountRef.current) {
      incrementCount();
      hasIncrementedCountRef.current = true;

      // Check count after increment
      const currentCount = count + 1; // Since count hasn't updated yet in this render
      if (currentCount === 3) {
        actions.modal.openBookCallModal(null);
      }
    }
  }, [hasShowAnimation, isSubscribed, count]);

  const toggleModals = () => {
    actions.modal.openChatbotSearchModal(null);
  };

  const toggleAdvancedStatsModals = () => {
    hapticsImpactLight();
    Mixpanel.trackEvent("opne_stat_glossary_screen", {
      name: "User opening stat glossary (Advanced stats) screen",
    });
    actions.modal.openAdvancedStatsModal(null);
    setHasSeenPromo(true);
    actions.modal.closeAdvancedStatsPromoPopup(null);
  };

  const toggleLeagueModal = () => {
    hapticsImpactLight();
    if (!leagueAccess) {
      navigate('/chatbot-paywall-v2');
      return;
    }
    if (filteredLeagueData.length > 0) {
      actions.modal.openAddNewLeagueModal(null);

    } else {
      actions.modal.openLeagueModal(null);
    }
    Mixpanel.trackEvent("league_sync_icon_selected", {
      name: "User opening league selection modal",
    });

  };

  // Simplified animation variant - single fade in for the whole component
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeInOut",
      },
    },
  };

  return (
    <>
      <motion.div
        className={`flex flex-col h-full`}
        initial={hasShowAnimation ? "hidden" : "visible"}
        animate="visible"
        variants={hasShowAnimation ? containerVariants : {}}
      >
        {showMaintenanceScreen ? (
          <>
            <div className="flex items-center justify-center h-full w-full relative">
              <img
                src={chatbotDownBg}
                alt="chatbotDownBg"
                className="absolute top-0 left-0 w-full h-full object-cover z-0"
              />
              <img
                src={chatbotDown}
                alt="chatbotDown"
                className="max-w-[80%] z-10 relative"
              />
            </div>

            <div
              id="footer-chat"
              className="flex flex-col justify-start transition-all duration-400 w-full mt-auto opacity-50"
            >
              <div className="px-5 transition-all duration-500">
                {" "}
                <div className="w-full h-[52px] flex items-center bg-white/20 rounded-[57px] mt-5 mb-3 justify-between">
                  <div className="px-5">
                    <div className="relative flex items-center w-[18px] h-[18px]">
                      <img
                        src={selectplatform}
                        alt="selectplatform"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  <div className="w-full h-full flex items-center justify-center cursor-pointer">
                    <span className="text-[16px] font-medium text-white/90">
                      Ask RotoBot
                    </span>
                  </div>

                  <div className="px-5">
                    <div className="w-[14.75px] h-[17.67px] flex items-center justify-center">
                      <img
                        src={advancedStats}
                        alt="advancedStats"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <AiEmptyChat dataItems={dataItems} />
            <div
              id="footer-chat"
              className="flex flex-col justify-start transition-all duration-400 w-full mt-auto"
            >
              <div className="px-5 transition-all duration-500">
                <div className="flex-col space-y-[12px]">
                  <Paragraph
                    text={`Hey, ${firstName}`}
                    className="!text-[24px] font-semibold leading-[100%]"
                    ParagraphType="PragraghSemiBold400"
                    color="textPrimary"
                  />
                  <Paragraph
                    text="What would you like to do?"
                    className="!text-[12px] leading-[100%] opacity-70"
                    ParagraphType="PragraghSemiBold400"
                    color="textPrimary"
                  />
                </div>

                <div className="w-full h-[52px] flex items-center bg-white/20 rounded-[57px] mt-5 mb-3 justify-between">
                  <div className="px-5" onClick={toggleLeagueModal}>
                    <div className="relative flex items-center w-[18px] h-[18px]">
                      <img
                        src={selectplatform}
                        alt="selectplatform"
                        className="w-full h-full object-contain"
                      />
                      {selectedLeague && (
                        <div className="absolute right-[-3px] top-[-5px]">
                          <LeagueActiveDot />
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className="w-full h-full flex items-center justify-center cursor-pointer"
                    onClick={toggleModals}
                  >
                    <span className="text-[16px] font-medium text-white/90">
                      Ask RotoBot
                    </span>
                  </div>

                  <div className="px-5 relative">
                    {/* Position AdvancedStatsPromo above the advanced stats button with full width */}
                    {isAdvancedStatsPromoPopup && (
                      <div className="absolute bottom-full right-0 mb-[10px] z-10 ">
                        <div className="whitespace-nowrap">
                          <AdvancedStatsPromo />
                        </div>
                      </div>
                    )}

                    <div
                      className="w-[20px] h-[20px] flex items-center justify-center"
                      onClick={toggleAdvancedStatsModals}
                    >
                      <img
                        src={advancedStats}
                        alt="advancedStats"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </>
  );
};
